import { getLineNet, roundMoney } from './quote-totals';

export interface GaebEditorSummaryEntry {
  id?: string | number;
  type?: string;
  hierarchyLevel?: number;
  positionNumber?: string;
  gewerk?: string;
  name?: string;
  shortText?: string;
  ourPrice?: number;
  originalPrice?: number;
  adjustedQuantity?: number;
  originalQuantity?: number;
  menge?: number;
  isBedarfsposition?: boolean;
  isAlternativposition?: boolean;
}

export interface GaebEditorLineSummary {
  effectiveUnitPrice: number;
  originalUnitPrice: number;
  quantity: number;
  effectiveTotal: number;
  originalTotal: number;
  excludedFromMain: boolean;
}

export interface GaebEditorGroupSummary {
  key: string;
  name: string;
  count: number;
  originalTotal: number;
  effectiveTotal: number;
}

export interface GaebEditorSummary {
  linesById: Map<string, GaebEditorLineSummary>;
  groups: GaebEditorGroupSummary[];
  totalItemCount: number;
  pricedItemCount: number;
  mainTotal: number;
  originalMainTotal: number;
  bedarfTotal: number;
  alternativeTotal: number;
  combinedMainAndBedarfTotal: number;
}

function finiteNumber(value: unknown): number | undefined {
  return typeof value === 'number' && Number.isFinite(value) ? value : undefined;
}

export function getEffectiveGaebQuantity(entry: GaebEditorSummaryEntry): number {
  return finiteNumber(entry.adjustedQuantity)
    ?? finiteNumber(entry.originalQuantity)
    ?? finiteNumber(entry.menge)
    ?? 0;
}

export function getOriginalGaebUnitPrice(entry: GaebEditorSummaryEntry): number {
  return finiteNumber(entry.originalPrice) ?? 0;
}

export function getEffectiveGaebUnitPrice(entry: GaebEditorSummaryEntry): number {
  return finiteNumber(entry.ourPrice) ?? getOriginalGaebUnitPrice(entry);
}

function groupKey(entry: GaebEditorSummaryEntry): string {
  const gewerk = entry.gewerk?.trim();
  if (gewerk) return gewerk;
  const root = entry.positionNumber?.split('.')[0]?.trim();
  return root || 'Allgemein';
}

export function buildGaebEditorSummary(
  entries: ReadonlyArray<GaebEditorSummaryEntry>,
): GaebEditorSummary {
  const titleNames = new Map<string, string>();
  for (const entry of entries) {
    if (entry.type !== 'title' || entry.hierarchyLevel !== 0) continue;
    const key = groupKey(entry);
    titleNames.set(key, entry.name?.trim() || entry.shortText?.trim() || key);
  }

  const linesById = new Map<string, GaebEditorLineSummary>();
  const groupsByKey = new Map<string, GaebEditorGroupSummary>();
  let totalItemCount = 0;
  let pricedItemCount = 0;
  let mainTotal = 0;
  let originalMainTotal = 0;
  let bedarfTotal = 0;
  let alternativeTotal = 0;

  entries.forEach((entry, index) => {
    if (entry.type !== 'item') return;

    const quantity = getEffectiveGaebQuantity(entry);
    const effectiveUnitPrice = getEffectiveGaebUnitPrice(entry);
    const originalUnitPrice = getOriginalGaebUnitPrice(entry);
    const effectiveTotal = getLineNet({ preis: effectiveUnitPrice, menge: quantity });
    const originalTotal = getLineNet({ preis: originalUnitPrice, menge: quantity });
    const isBedarf = Boolean(entry.isBedarfsposition);
    const isAlternative = !isBedarf && Boolean(entry.isAlternativposition);
    const excludedFromMain = isBedarf || isAlternative;

    linesById.set(String(entry.id ?? index), {
      effectiveUnitPrice,
      originalUnitPrice,
      quantity,
      effectiveTotal,
      originalTotal,
      excludedFromMain,
    });

    totalItemCount += 1;
    if (finiteNumber(entry.ourPrice) !== undefined) pricedItemCount += 1;

    if (isBedarf) {
      bedarfTotal += effectiveTotal;
    } else if (isAlternative) {
      alternativeTotal += effectiveTotal;
    } else {
      mainTotal += effectiveTotal;
      originalMainTotal += originalTotal;
    }

    const key = groupKey(entry);
    let group = groupsByKey.get(key);
    if (!group) {
      group = {
        key,
        name: titleNames.get(key) || key,
        count: 0,
        originalTotal: 0,
        effectiveTotal: 0,
      };
      groupsByKey.set(key, group);
    }
    group.count += 1;
    if (!excludedFromMain) {
      group.originalTotal += originalTotal;
      group.effectiveTotal += effectiveTotal;
    }
  });

  const groups = Array.from(groupsByKey.values()).map((group) => ({
    ...group,
    originalTotal: roundMoney(group.originalTotal),
    effectiveTotal: roundMoney(group.effectiveTotal),
  }));
  mainTotal = roundMoney(mainTotal);
  originalMainTotal = roundMoney(originalMainTotal);
  bedarfTotal = roundMoney(bedarfTotal);
  alternativeTotal = roundMoney(alternativeTotal);

  return {
    linesById,
    groups,
    totalItemCount,
    pricedItemCount,
    mainTotal,
    originalMainTotal,
    bedarfTotal,
    alternativeTotal,
    combinedMainAndBedarfTotal: roundMoney(mainTotal + bedarfTotal),
  };
}
